/**
 * Avatar Composable
 * Centralized avatar URL generation with consistent priority fallback logic
 *
 * Priority Order:
 * 1. Uploaded avatar (user's custom uploaded photo)
 * 2. Google avatar (from OAuth)
 * 3. UI-Avatars placeholder (name initials)
 */

import { computed } from 'vue';

// Standard configuration
const AVATAR_CONFIG = {
  // UI-Avatars settings
  baseUrl: 'https://ui-avatars.com/api/',
  backgroundColor: '7B8FD4',  // Primary brand color (matches --primary-color)
  fallbackBackgroundColor: '94a3b8',  // Gray for error fallback
  textColor: 'fff',

  // Standard sizes
  sizes: {
    xs: 32,    // Extra small (table cells, compact lists)
    sm: 40,    // Small (top bar, nav)
    md: 60,    // Medium (org chart, directory)
    lg: 80,    // Large (network cards, connections)
    xl: 120,   // Extra large (workspace profile)
    xxl: 200   // Profile page hero
  }
};

/**
 * Get API base URL for uploaded avatars
 * In production, returns empty string (same-origin relative URLs)
 * In development, returns VITE_API_URL or localhost:3000
 * @returns {string} API base URL
 */
function getApiBaseUrl() {
  // If VITE_API_URL is explicitly set, use it
  // If empty or not set in production, use same-origin (empty string)
  // In development, fall back to localhost:3000
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl !== undefined && apiUrl !== '') {
    return apiUrl;
  }
  // In production (built code), VITE_API_URL will be empty string, use same-origin
  if (import.meta.env.PROD) {
    return '';
  }
  // In development, fall back to localhost
  return 'http://localhost:3000';
}

/**
 * Generate initials from first and last name
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} Initials (e.g., "JD")
 */
export function getInitials(firstName, lastName) {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Generate full name from user object or separate names
 * @param {object|string} userOrFirstName - User object or first name
 * @param {string} lastName - Last name (if first param is string)
 * @returns {string} Full name
 */
function getFullName(userOrFirstName, lastName) {
  if (typeof userOrFirstName === 'object' && userOrFirstName !== null) {
    const first = userOrFirstName.first_name || userOrFirstName.firstName || '';
    const last = userOrFirstName.last_name || userOrFirstName.lastName || '';
    return `${first} ${last}`.trim() || 'User';
  }
  return `${userOrFirstName || ''} ${lastName || ''}`.trim() || 'User';
}

/**
 * Generate UI-Avatars placeholder URL
 * @param {string} name - Name for initials
 * @param {number} size - Avatar size in pixels
 * @param {string} backgroundColor - Background color (hex without #)
 * @returns {string} UI-Avatars URL
 */
export function getPlaceholderUrl(name, size = AVATAR_CONFIG.sizes.md, backgroundColor = AVATAR_CONFIG.backgroundColor) {
  const encodedName = encodeURIComponent(name || 'User');
  return `${AVATAR_CONFIG.baseUrl}?name=${encodedName}&background=${backgroundColor}&color=${AVATAR_CONFIG.textColor}&size=${size}`;
}

/**
 * Generate fallback placeholder URL (gray background for error states)
 * @param {string} name - Name for initials
 * @param {number} size - Avatar size in pixels
 * @returns {string} UI-Avatars URL with gray background
 */
export function getFallbackPlaceholderUrl(name, size = AVATAR_CONFIG.sizes.md) {
  return getPlaceholderUrl(name, size, AVATAR_CONFIG.fallbackBackgroundColor);
}

/**
 * Get avatar URL with priority fallback logic
 * Priority: uploaded_avatar > avatar (Google) > UI-Avatars placeholder
 *
 * @param {object} user - User object with avatar fields
 * @param {string} user.uploaded_avatar - User's uploaded avatar path
 * @param {string} user.avatar - Google OAuth avatar URL
 * @param {string} user.first_name - First name
 * @param {string} user.last_name - Last name
 * @param {object} options - Options
 * @param {number} options.size - Avatar size (default: 60)
 * @param {boolean} options.includeApiUrl - Whether to prepend API URL to uploaded avatars (default: true)
 * @returns {string} Avatar URL
 */
export function getAvatarUrl(user, options = {}) {
  const {
    size = AVATAR_CONFIG.sizes.md,
    includeApiUrl = true
  } = options;

  if (!user) {
    return getPlaceholderUrl('User', size);
  }

  // Priority 1: Uploaded avatar
  if (user.uploaded_avatar) {
    // Check if it's already a full URL
    if (user.uploaded_avatar.startsWith('http')) {
      return user.uploaded_avatar;
    }
    // Prepend API URL if needed
    if (includeApiUrl) {
      return `${getApiBaseUrl()}${user.uploaded_avatar}`;
    }
    return user.uploaded_avatar;
  }

  // Priority 2: Google OAuth avatar
  if (user.avatar) {
    return user.avatar;
  }

  // Priority 3: UI-Avatars placeholder with initials
  const fullName = getFullName(user);
  return getPlaceholderUrl(fullName, size);
}

/**
 * Create an error handler function for avatar images
 * Falls back to UI-Avatars placeholder on image load error
 *
 * @param {object} user - User object
 * @param {number} size - Avatar size
 * @returns {function} Error handler function
 */
export function createAvatarErrorHandler(user, size = AVATAR_CONFIG.sizes.md) {
  return (event) => {
    const fullName = getFullName(user);
    event.target.src = getFallbackPlaceholderUrl(fullName, size);
    // Prevent infinite error loop
    event.target.onerror = null;
  };
}

/**
 * Vue composable for avatar management
 * Provides reactive avatar URL computation and error handling
 *
 * @param {import('vue').Ref|function} userRef - Reactive user reference or getter function
 * @param {object} options - Options
 * @param {number} options.size - Avatar size (default: 60)
 * @param {boolean} options.includeApiUrl - Whether to prepend API URL (default: true)
 * @returns {object} Composable return object
 */
export function useAvatar(userRef, options = {}) {
  const {
    size = AVATAR_CONFIG.sizes.md,
    includeApiUrl = true
  } = options;

  // Computed avatar URL
  const avatarUrl = computed(() => {
    const user = typeof userRef === 'function' ? userRef() : userRef?.value;
    return getAvatarUrl(user, { size, includeApiUrl });
  });

  // Computed initials
  const initials = computed(() => {
    const user = typeof userRef === 'function' ? userRef() : userRef?.value;
    if (!user) return '?';
    return getInitials(
      user.first_name || user.firstName,
      user.last_name || user.lastName
    );
  });

  // Computed full name
  const fullName = computed(() => {
    const user = typeof userRef === 'function' ? userRef() : userRef?.value;
    return getFullName(user);
  });

  // Error handler for image load failures
  const onAvatarError = (event) => {
    const user = typeof userRef === 'function' ? userRef() : userRef?.value;
    event.target.src = getFallbackPlaceholderUrl(getFullName(user), size);
    event.target.onerror = null;
  };

  // Get placeholder URL for this user
  const placeholderUrl = computed(() => {
    const user = typeof userRef === 'function' ? userRef() : userRef?.value;
    return getPlaceholderUrl(getFullName(user), size);
  });

  return {
    avatarUrl,
    initials,
    fullName,
    onAvatarError,
    placeholderUrl
  };
}

// Export configuration for reference
export const AVATAR_SIZES = AVATAR_CONFIG.sizes;
export const AVATAR_COLORS = {
  primary: AVATAR_CONFIG.backgroundColor,
  fallback: AVATAR_CONFIG.fallbackBackgroundColor,
  text: AVATAR_CONFIG.textColor
};

export default useAvatar;
